import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { buildFormFieldPalette, PaletteFieldItem } from './formFieldPalette'
import './FormFields.scss'

interface IProps {
  formElements: any
  setFormElements: any
  selectedField: any
  ThemeTextColor: any
  currentPageIndex: number
  addNewPage: () => void
}

const getFieldTypeLabel = (formInput: Record<string, unknown>) => {
  if (formInput.type === 'button' && formInput.buttonAction) {
    return String(formInput.buttonAction)
  }
  return String(formInput.type || 'field')
}

const FormFields: React.FC<IProps> = ({
  formElements,
  setFormElements,
  selectedField,
  ThemeTextColor,
  currentPageIndex,
  addNewPage
}) => {
  const [search, setSearch] = useState('')
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({})
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const sections = useMemo(() => buildFormFieldPalette(ThemeTextColor), [ThemeTextColor])

  const filteredSections = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return sections

    return sections
      .map((section) => ({
        ...section,
        subComponent: section.subComponent.filter((component) => {
          const haystack = [
            component.fieldname,
            component.keywords,
            getFieldTypeLabel(component.formInput)
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return haystack.includes(term)
        })
      }))
      .filter((section) => section.subComponent.length > 0)
  }, [sections, search])

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  const FormFieldHandler = (component: PaletteFieldItem) => {
    const formInput = component.formInput

    if (formInput.type === 'addPage') {
      addNewPage()
      return
    }

    const allPages = formElements
    const currentPageElements = Array.isArray(allPages[currentPageIndex])
      ? allPages[currentPageIndex]
      : []

    if (formInput.type === 'prevPageButton') {
      if (allPages.length <= 1) {
        toast.warn('Previous page button requires at least two pages in the form.')
        return
      }
      if (currentPageIndex === 0) {
        toast.warn('Previous page button cannot be added to the first page.')
        return
      }
    }

    if (formInput.type === 'nextPageButton') {
      if (allPages.length <= 1) {
        toast.warn('Next page button requires at least two pages in the form.')
        return
      }
      if (currentPageIndex === allPages.length - 1) {
        toast.warn('Next page button cannot be added to the last page.')
        return
      }
    }

    if (formInput.buttonAction === 'previous') {
      if (allPages.length <= 1) {
        toast.warn('Previous button requires at least two pages in the form.')
        return
      }
      if (currentPageIndex === 0) {
        toast.warn('Previous button cannot be added to the first page.')
        return
      }
    }

    if (formInput.buttonAction === 'next') {
      if (allPages.length <= 1) {
        toast.warn('Next button requires at least two pages in the form.')
        return
      }
      if (currentPageIndex === allPages.length - 1) {
        toast.warn('Next button cannot be added to the last page.')
        return
      }
    }

    let maxFieldId = 0
    allPages.forEach((page: any) => {
      page.forEach((field: any) => {
        if (field.fieldId > maxFieldId) {
          maxFieldId = field.fieldId
        }
      })
    })

    let indexedFormInput: any = { ...formInput, fieldId: maxFieldId + 1 }

    if (formInput.type === 'buttonRow') {
      indexedFormInput = {
        ...indexedFormInput,
        buttons: ((indexedFormInput.buttons as any[]) || []).map((button: any, index: number) => ({
          ...button,
          id: `btn-${indexedFormInput.fieldId}-${index}-${Date.now()}`
        }))
      }
    }

    if (formInput.type === 'banner' || formInput.type === 'captcha') {
      const existingIndex = currentPageElements.findIndex(
        (element: any) => element.type === formInput.type
      )

      if (existingIndex !== -1) {
        toast.warning(`${formInput.type} is already present on this page.`)
      } else {
        const newFormElements = [...allPages]
        newFormElements[currentPageIndex] = [...currentPageElements, indexedFormInput]
        setFormElements(newFormElements)
      }
      return
    }

    const updatedCurrentPageElements =
      selectedField !== null && selectedField >= 0 && selectedField < currentPageElements.length
        ? [
            ...currentPageElements.slice(0, selectedField + 1),
            indexedFormInput,
            ...currentPageElements.slice(selectedField + 1)
          ]
        : [...currentPageElements, indexedFormInput]

    const newFormElements = [...allPages]
    newFormElements[currentPageIndex] = updatedCurrentPageElements
    setFormElements(newFormElements)
  }

  return (
    <div className='form-field-palette'>
      <div className='form-field-palette__search-wrap position-relative'>
        <i className='bi bi-search form-field-palette__search-icon' />
        <input
          type='search'
          className='form-control form-field-palette__search'
          placeholder='Search fields...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filteredSections.length === 0 ? (
        <div className='form-field-palette__empty'>No fields match your search.</div>
      ) : (
        filteredSections.map((section) => {
          const isCollapsed = collapsedSections[section.id] ?? false

          return (
            <div key={section.id} className='form-field-palette__section'>
              <button
                type='button'
                className='form-field-palette__section-header'
                onClick={() => toggleSection(section.id)}
              >
                <span className='form-field-palette__section-title'>
                  <span
                    className='form-field-palette__section-dot'
                    style={{ backgroundColor: section.color }}
                  />
                  <span className='form-field-palette__section-label'>{section.heading}</span>
                  <span className='form-field-palette__section-count'>
                    {section.subComponent.length}
                  </span>
                </span>
                <i
                  className={`bi bi-chevron-down form-field-palette__section-chevron ${
                    isCollapsed ? '' : 'form-field-palette__section-chevron--open'
                  }`}
                />
              </button>

              {!isCollapsed && (
                <div className='form-field-palette__grid'>
                  {section.subComponent.map((component) => {
                    const cardId = `${section.id}-${component.id}`
                    const isHovered = hoveredId === cardId

                    return (
                      <button
                        key={cardId}
                        type='button'
                        className='form-field-palette__card'
                        style={{
                          backgroundColor: section.background,
                          borderColor: isHovered ? section.color : section.borderColor,
                          color: section.color
                        }}
                        onClick={() => FormFieldHandler(component)}
                        onMouseEnter={() => setHoveredId(cardId)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        <i className={`${component.icon} form-field-palette__card-icon`} />
                        <span className='form-field-palette__card-label'>
                          {component.fieldname}
                        </span>
                        <span className='form-field-palette__card-type'>
                          {getFieldTypeLabel(component.formInput)}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}

export default FormFields
